
/* ============================== src/hooks/useTaskActions.js ============================== */
/**
 * Хук действий над задачей (перемещение по статусам, удаление).
 * Содержит UX-проверки для предотвращения запрещенных действий
 * и вызова соответствующих алертов.
 */
import { deleteTask, changeTaskStatus } from "../utils/taskActions";
import { useEventCallback } from "./useEventCallback";
import {
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} from "../api/queries/tasks";

export function useTaskActions({ ur, db, toast }) {
  const updateMutation = useUpdateTaskMutation();
  const deleteMutation = useDeleteTaskMutation();

  const handleMove = useEventCallback(async (taskId, newStatus) => {
    const task = db.tasks.find((t) => t.id === taskId);
    if (!task) return;

    // Если статус не меняется, ничего не делаем
    if (task.status === newStatus) return;

    const isAssignee = ur && task.assigneeId === ur.id;
    const isAuthor = ur && task.creatorId === ur.id;
    const restrictedStatuses = ["new", "cancelled", "closed"];

    // 1. Специфичный алерт для исполнителя (если он не автор задачи)
    if (isAssignee && !isAuthor && restrictedStatuses.includes(newStatus)) {
      toast(
        "Вы не можете перевести задачу в статус «Новая», «Отменена» или «Закрыта». Это может сделать только автор задачи или администратор.",
        "error",
      );
      return; // Прерываем выполнение, задача вернется на место на Канбане
    }

    // 2. Старая проверка базовых прав (например, исполнитель пытается перепрыгнуть статусы)
    const body = changeTaskStatus({ task, newStatus });
    if (body === false) {
      toast("У вас нет прав на изменение статуса этой задачи.", "error");
      return;
    }
    if (!body) return;

    // 3. Отправка на сервер
    try {
      await updateMutation.mutateAsync({
        id: taskId,
        body: { status: newStatus },
        needsConfirmation: true,
      });
    } catch (err) {
      toast(err.message || "Ошибка смены статуса", "error");
    }
  });

  const handleDelete = useEventCallback(async (task) => {
    const id = deleteTask({ task });
    if (!id) return;
    try {
      await deleteMutation.mutateAsync({ id });
      toast(`Задача «${task.title}» удалена`, "success");
    } catch (err) {
      toast(err.message || "Ошибка удаления задачи", "error");
    }
  });

  return { handleMove, handleDelete };
}
